import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import competitionService from '../services/competitionService';
import './ReelViewerPage.css';

const COMPETITION_BATCH_SIZE = 8;
const PREFETCH_ITEM_THRESHOLD = 3;
const CURATED_SUBMISSIONS_LIMIT = 4;
const SWIPE_HINT_FADE_DELAY = 3800;
const MOBILE_LONG_PRESS_DELAY = 450;
const MOBILE_HINT_VISIBLE_MS = 1200;

const ReelViewerPage = () => {
  const { competitionId: routeCompetitionId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const initialCompetitionId = searchParams.get('initialCompetitionId') || routeCompetitionId || null;

  const [feedItems, setFeedItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const [snackbar, setSnackbar] = useState({
    visible: false,
    message: '',
    competitionId: null
  });
  const [showSwipeHint, setShowSwipeHint] = useState(true);
  const [mobileHintAction, setMobileHintAction] = useState('');
  const [savedCompetitionIds, setSavedCompetitionIds] = useState(() => {
    try {
      const raw = window.localStorage.getItem('savedChallengeIds');
      const parsed = raw ? JSON.parse(raw) : [];
      return new Set(Array.isArray(parsed) ? parsed : []);
    } catch (error) {
      return new Set();
    }
  });

  const scrollContainerRef = useRef(null);
  const queueRef = useRef([]);
  const queueOffsetRef = useRef(0);
  const queueHasMoreRef = useRef(true);
  const queueLoadingRef = useRef(false);
  const seenCompetitionIdsRef = useRef(new Set());
  const queueCursorRef = useRef(0);
  const appendInProgressRef = useRef(false);
  const activeCompetitionRef = useRef(null);
  const feedLengthRef = useRef(0);
  const swipeHintTimerRef = useRef(null);
  const mobileLongPressTimerRef = useRef(null);
  const mobileHintTimerRef = useRef(null);
  const iframeRefs = useRef(new Map());
  const userPausedByIdRef = useRef(new Map());

  useEffect(() => {
    feedLengthRef.current = feedItems.length;
  }, [feedItems.length]);

  const isMobileViewport = useCallback(() => {
    return (window.innerWidth || 0) <= 768;
  }, []);

  const showMobileHint = useCallback((actionKey) => {
    if (!isMobileViewport()) {
      return;
    }

    setMobileHintAction(actionKey);
    if (mobileHintTimerRef.current) {
      window.clearTimeout(mobileHintTimerRef.current);
    }

    mobileHintTimerRef.current = window.setTimeout(() => {
      setMobileHintAction('');
    }, MOBILE_HINT_VISIBLE_MS);
  }, [isMobileViewport]);

  const handleIconPressStart = useCallback((actionKey) => {
    if (!isMobileViewport()) {
      return;
    }

    if (mobileLongPressTimerRef.current) {
      window.clearTimeout(mobileLongPressTimerRef.current);
    }

    mobileLongPressTimerRef.current = window.setTimeout(() => {
      showMobileHint(actionKey);
    }, MOBILE_LONG_PRESS_DELAY);
  }, [isMobileViewport, showMobileHint]);

  const handleIconPressEnd = useCallback(() => {
    if (mobileLongPressTimerRef.current) {
      window.clearTimeout(mobileLongPressTimerRef.current);
      mobileLongPressTimerRef.current = null;
    }
  }, []);

  const postPlayerCommand = useCallback((playerId, func, args = []) => {
    const iframe = iframeRefs.current.get(playerId);
    if (!iframe || !iframe.contentWindow) {
      return;
    }

    iframe.contentWindow.postMessage(
      JSON.stringify({
        event: 'command',
        func,
        args
      }),
      '*'
    );
  }, []);

  const syncPlaybackByActiveIndex = useCallback((indexToActivate = activeIndex) => {
    const activeFeedItem = feedItems[indexToActivate];
    const activeId = activeFeedItem && activeFeedItem.type !== 'transition' ? activeFeedItem.id : null;

    feedItems.forEach((item) => {
      if (!item || item.type === 'transition' || !item.videoId) {
        return;
      }

      if (item.id === activeId) {
        const isUserPaused = userPausedByIdRef.current.get(item.id) === true;
        if (!isUserPaused) {
          postPlayerCommand(item.id, 'unMute');
          postPlayerCommand(item.id, 'setVolume', [100]);
          postPlayerCommand(item.id, 'playVideo');
        }
      } else {
        postPlayerCommand(item.id, 'pauseVideo');
      }
    });
  }, [activeIndex, feedItems, postPlayerCommand]);

  const fetchCompetitionBatch = useCallback(async () => {
    if (queueLoadingRef.current || !queueHasMoreRef.current) {
      return;
    }

    queueLoadingRef.current = true;
    try {
      const data = await competitionService.getAll({
        limit: COMPETITION_BATCH_SIZE,
        offset: queueOffsetRef.current
      });

      const competitions = Array.isArray(data.competitions) ? data.competitions : [];
      queueOffsetRef.current += competitions.length;
      queueHasMoreRef.current = typeof data.hasMore === 'boolean' ? data.hasMore : competitions.length === COMPETITION_BATCH_SIZE;

      competitions.forEach((competition) => {
        if (!competition || !competition.id || seenCompetitionIdsRef.current.has(competition.id)) {
          return;
        }

        seenCompetitionIdsRef.current.add(competition.id);
        queueRef.current.push(competition);
      });
    } finally {
      queueLoadingRef.current = false;
    }
  }, []);

  const enqueueCompetitionById = useCallback(async (competitionId) => {
    if (!competitionId || seenCompetitionIdsRef.current.has(competitionId)) {
      return;
    }

    const data = await competitionService.getById(competitionId);
    const competition = data?.competition || data;

    if (!competition || !competition.id || seenCompetitionIdsRef.current.has(competition.id)) {
      return;
    }

    seenCompetitionIdsRef.current.add(competition.id);
    queueRef.current.push(competition);
  }, []);

  const getNextCompetition = useCallback(async () => {
    if (queueCursorRef.current >= queueRef.current.length) {
      if (queueHasMoreRef.current) {
        await fetchCompetitionBatch();
      }

      if (queueCursorRef.current >= queueRef.current.length) {
        if (queueRef.current.length === 0) {
          return null;
        }

        // Keep feed infinite by looping over discovered competitions.
        queueCursorRef.current = 0;
      }
    }

    const competition = queueRef.current[queueCursorRef.current] || null;
    queueCursorRef.current += 1;

    if (queueHasMoreRef.current && queueRef.current.length - queueCursorRef.current < 2) {
      fetchCompetitionBatch();
    }

    return competition;
  }, [fetchCompetitionBatch]);

  const appendCompetitionContent = useCallback(async () => {
    if (appendInProgressRef.current) {
      return;
    }

    appendInProgressRef.current = true;
    try {
      const competition = await getNextCompetition();
      if (!competition) {
        return;
      }

      const cards = await buildCardsForCompetition(competition);
      if (cards.length === 0) {
        return;
      }

      setFeedItems((previousItems) => {
        const transitionCard = previousItems.length > 0
          ? [createTransitionCard(competition)]
          : [];
        return [...previousItems, ...transitionCard, ...cards];
      });
    } catch (appendError) {
      console.error('Failed to append reel content:', appendError);
      if (feedLengthRef.current === 0) {
        setError('Unable to load reel feed right now. Please try again.');
      }
    } finally {
      appendInProgressRef.current = false;
    }
  }, [getNextCompetition]);

  const initializeFeed = useCallback(async () => {
    setLoading(true);
    setError('');
    setFeedItems([]);
    setActiveIndex(0);
    setSnackbar({ visible: false, message: '', competitionId: null });

    queueRef.current = [];
    queueOffsetRef.current = 0;
    queueHasMoreRef.current = true;
    queueLoadingRef.current = false;
    queueCursorRef.current = 0;
    appendInProgressRef.current = false;
    seenCompetitionIdsRef.current = new Set();
    activeCompetitionRef.current = null;

    try {
      if (initialCompetitionId) {
        await enqueueCompetitionById(initialCompetitionId);
      }

      await fetchCompetitionBatch();

      if (queueRef.current.length === 0) {
        setError('No competitions available to preview right now.');
        return;
      }

      await appendCompetitionContent();
      await appendCompetitionContent();
    } catch (loadError) {
      console.error('Failed to initialize reel viewer:', loadError);
      setError('Unable to start reel viewer. Please refresh and try again.');
    } finally {
      setLoading(false);
    }
  }, [appendCompetitionContent, enqueueCompetitionById, fetchCompetitionBatch, initialCompetitionId]);

  useEffect(() => {
    initializeFeed();
  }, [initializeFeed]);

  useEffect(() => {
    if (swipeHintTimerRef.current) {
      window.clearTimeout(swipeHintTimerRef.current);
    }

    swipeHintTimerRef.current = window.setTimeout(() => {
      setShowSwipeHint(false);
    }, SWIPE_HINT_FADE_DELAY);

    return () => {
      if (swipeHintTimerRef.current) {
        window.clearTimeout(swipeHintTimerRef.current);
      }

      if (mobileLongPressTimerRef.current) {
        window.clearTimeout(mobileLongPressTimerRef.current);
      }

      if (mobileHintTimerRef.current) {
        window.clearTimeout(mobileHintTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!feedItems.length) {
      return;
    }

    const activeItem = feedItems[activeIndex];
    if (!activeItem || !activeItem.competitionId || activeItem.type === 'transition') {
      return;
    }

    if (activeCompetitionRef.current !== activeItem.competitionId) {
      activeCompetitionRef.current = activeItem.competitionId;
      setSnackbar({
        visible: true,
        message: `Now viewing: ${activeItem.competitionName}`,
        competitionId: activeItem.competitionId
      });
    }

    if (feedItems.length - activeIndex <= PREFETCH_ITEM_THRESHOLD) {
      appendCompetitionContent();
    }
  }, [activeIndex, appendCompetitionContent, feedItems]);

  useEffect(() => {
    syncPlaybackByActiveIndex(activeIndex);
  }, [activeIndex, feedItems, syncPlaybackByActiveIndex]);

  useEffect(() => {
    const handleYouTubeMessage = (event) => {
      if (!event || typeof event.data !== 'string') {
        return;
      }

      let payload;
      try {
        payload = JSON.parse(event.data);
      } catch (error) {
        return;
      }

      if (!payload || !payload.event) {
        return;
      }

      // YouTube emits state changes in different shapes:
      // 1) { event: 'onStateChange', id, info: <state> }
      // 2) { event: 'infoDelivery', id, info: { playerState: <state> } }
      const playerId = payload.id;
      const state = payload.event === 'onStateChange'
        ? payload.info
        : payload.info && typeof payload.info === 'object'
          ? payload.info.playerState
          : undefined;

      if (typeof state !== 'number') {
        return;
      }

      if (!playerId) {
        return;
      }

      if (state === 2) {
        userPausedByIdRef.current.set(playerId, true);
      }

      if (state === 1) {
        userPausedByIdRef.current.set(playerId, false);
      }

      // If video ended and user didn't explicitly pause, restart it.
      if (state === 0 && userPausedByIdRef.current.get(playerId) !== true) {
        postPlayerCommand(playerId, 'unMute');
        postPlayerCommand(playerId, 'setVolume', [100]);
        postPlayerCommand(playerId, 'playVideo');
      }
    };

    window.addEventListener('message', handleYouTubeMessage);
    return () => window.removeEventListener('message', handleYouTubeMessage);
  }, [postPlayerCommand]);

  useEffect(() => {
    if (!snackbar.visible) {
      return undefined;
    }

    const timer = window.setTimeout(() => {
      setSnackbar((currentState) => ({ ...currentState, visible: false }));
    }, 3200);

    return () => window.clearTimeout(timer);
  }, [snackbar.visible]);

  const handleFeedScroll = (event) => {
    const viewportHeight = window.innerHeight || 1;
    const nextIndex = Math.round(event.currentTarget.scrollTop / viewportHeight);
    if (nextIndex !== activeIndex) {
      const currentItem = feedItems[activeIndex];
      if (currentItem && currentItem.id) {
        postPlayerCommand(currentItem.id, 'pauseVideo');
      }

      const nextItem = feedItems[nextIndex];
      if (nextItem && nextItem.id) {
        userPausedByIdRef.current.set(nextItem.id, false);
      }

      setActiveIndex(nextIndex);
    }

    setShowSwipeHint(false);
  };

  const handleFeedTap = () => {
    // Intentionally left blank. Controls remain visible at all times.
  };

  const handleBack = () => {
    navigate(-1);
  };

  const handleViewCompetition = (competitionId) => {
    if (!competitionId) {
      return;
    }

    navigate(`/competitions/${competitionId}`);
  };

  const handleSubmitEntry = (competitionId) => {
    if (!competitionId) {
      return;
    }

    setSnackbar({ visible: true, message: 'Opening submission flow...', competitionId });

    navigate(`/competitions/${competitionId}?openSubmit=1`);
  };

  const handleUseSound = (item) => {
    const sourceUrl = item?.sourceUrl || item?.videoUrl;
    if (!sourceUrl) {
      return;
    }

    setSnackbar({ visible: true, message: 'Opening YouTube...', competitionId: item?.competitionId || null });

    window.open(sourceUrl, '_blank', 'noopener,noreferrer');
  };

  const copyText = async (textToCopy, successMessage) => {
    if (!textToCopy) {
      return;
    }

    try {
      await navigator.clipboard.writeText(textToCopy);
      setSnackbar((currentState) => ({
        ...currentState,
        visible: true,
        message: successMessage || 'Copied'
      }));
    } catch (copyError) {
      console.error('Copy failed:', copyError);
    }
  };

  const handleCopyHashtag = (hashtag) => {
    copyText(hashtag || '', 'Hashtag copied');
  };

  const handleSaveChallenge = (competitionId) => {
    if (!competitionId) {
      return;
    }

    setSavedCompetitionIds((currentValue) => {
      const nextValue = new Set(currentValue);
      nextValue.add(competitionId);
      window.localStorage.setItem('savedChallengeIds', JSON.stringify(Array.from(nextValue)));
      return nextValue;
    });

    setSnackbar({ visible: true, message: 'Saved', competitionId });
  };

  if (loading) {
    return (
      <div className="reel-viewer-loading">
        <div className="reel-spinner"></div>
        <p>Loading reel feed...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="reel-viewer-loading">
        <h2>Unable to Load Reel Viewer</h2>
        <p>{error}</p>
        <div className="error-actions">
          <button type="button" onClick={initializeFeed}>Retry</button>
          <button type="button" onClick={handleBack}>Back</button>
        </div>
      </div>
    );
  }

  const activeItem = feedItems[activeIndex] && feedItems[activeIndex].type !== 'transition'
    ? feedItems[activeIndex]
    : feedItems.find((item) => item.type !== 'transition') || null;
  const activeCompetitionName = activeItem?.competitionName || 'Reel Viewer';

  return (
    <div className="reel-viewer-page">
      <div className="reel-top-bar visible">
        <button type="button" className="reel-back-button" onClick={handleBack} aria-label="Go back">
          ←
        </button>
        <span className="reel-top-title" title={activeCompetitionName}>{activeCompetitionName}</span>
      </div>

      <div className="reel-feed" ref={scrollContainerRef} onScroll={handleFeedScroll} onClick={handleFeedTap}>
        {feedItems.map((item, index) => {
          if (item.type === 'transition') {
            return (
              <article key={item.id} className="reel-card transition-card">
                <div className="transition-content">
                  <small>Up next</small>
                  <h2>{item.competitionName}</h2>
                  <button type="button" onClick={() => handleViewCompetition(item.competitionId)}>
                    View Details
                  </button>
                </div>
              </article>
            );
          }

          return (
            <article key={item.id || `${item.competitionId}-${index}`} className="reel-card">
              <div className="reel-media-layer">
                {item.videoId ? (
                  <iframe
                    ref={(element) => {
                      if (element) {
                        iframeRefs.current.set(item.id, element);
                      } else {
                        iframeRefs.current.delete(item.id);
                        userPausedByIdRef.current.delete(item.id);
                      }
                    }}
                    src={`https://www.youtube.com/embed/${item.videoId}?autoplay=${index === activeIndex ? 1 : 0}&mute=0&modestbranding=1&playsinline=1&rel=0&loop=1&playlist=${item.videoId}&enablejsapi=1&controls=1&playerapiid=${encodeURIComponent(item.id)}`}
                    title={`${item.competitionName} reel`}
                    frameBorder="0"
                    allow="autoplay; encrypted-media; picture-in-picture"
                    allowFullScreen
                    onLoad={() => {
                      postPlayerCommand(item.id, 'addEventListener', ['onStateChange']);
                      if (index === activeIndex) {
                        userPausedByIdRef.current.set(item.id, false);
                        postPlayerCommand(item.id, 'unMute');
                        postPlayerCommand(item.id, 'setVolume', [100]);
                        postPlayerCommand(item.id, 'playVideo');
                      }
                    }}
                  ></iframe>
                ) : (
                  <div className="reel-fallback-panel">
                    <p>Video preview unavailable for this source.</p>
                    {item.sourceUrl && (
                      <button type="button" onClick={() => handleUseSound(item)}>
                        Open Source
                      </button>
                    )}
                  </div>
                )}
              </div>

              <div className="reel-icon-stack visible">
                <button
                  type="button"
                  className="reel-icon-button primary"
                  onClick={() => handleUseSound(item)}
                  onTouchStart={() => handleIconPressStart('sound')}
                  onTouchEnd={handleIconPressEnd}
                  onTouchCancel={handleIconPressEnd}
                  aria-label="Use sound"
                  title="Use sound"
                >
                  <span className="reel-icon-symbol" aria-hidden="true">🎵</span>
                  <span className={`reel-icon-label ${mobileHintAction === 'sound' ? 'hint-visible' : ''}`}>Use sound</span>
                </button>
                <button
                  type="button"
                  className="reel-icon-button"
                  onClick={() => handleSubmitEntry(item.competitionId)}
                  onTouchStart={() => handleIconPressStart('submit')}
                  onTouchEnd={handleIconPressEnd}
                  onTouchCancel={handleIconPressEnd}
                  aria-label="Submit entry"
                  title="Submit entry"
                >
                  <span className="reel-icon-symbol" aria-hidden="true">📤</span>
                  <span className={`reel-icon-label ${mobileHintAction === 'submit' ? 'hint-visible' : ''}`}>Submit</span>
                </button>
                <button
                  type="button"
                  className="reel-icon-button"
                  onClick={() => handleCopyHashtag(item.hashtag)}
                  onTouchStart={() => handleIconPressStart('hashtag')}
                  onTouchEnd={handleIconPressEnd}
                  onTouchCancel={handleIconPressEnd}
                  aria-label="Copy hashtag"
                  title="Copy hashtag"
                >
                  <span className="reel-icon-symbol" aria-hidden="true">📋</span>
                  <span className={`reel-icon-label ${mobileHintAction === 'hashtag' ? 'hint-visible' : ''}`}>Copy hashtag</span>
                </button>
                <button
                  type="button"
                  className="reel-icon-button"
                  onClick={() => handleSaveChallenge(item.competitionId)}
                  onTouchStart={() => handleIconPressStart('save')}
                  onTouchEnd={handleIconPressEnd}
                  onTouchCancel={handleIconPressEnd}
                  aria-label="Save challenge"
                  title="Save challenge"
                >
                  <span className="reel-icon-symbol" aria-hidden="true">{savedCompetitionIds.has(item.competitionId) ? '✅' : '💾'}</span>
                  <span className={`reel-icon-label ${mobileHintAction === 'save' ? 'hint-visible' : ''}`}>Save</span>
                </button>
              </div>

              <div className="reel-inline-link visible">
                <button type="button" onClick={() => handleViewCompetition(item.competitionId)}>
                  View Competition
                </button>
              </div>
            </article>
          );
        })}
      </div>

      {showSwipeHint && (
        <div className="reel-swipe-hint">Swipe to explore →</div>
      )}

      {snackbar.visible && (
        <div className="reel-snackbar" role="status" aria-live="polite">
          <span>{snackbar.message}</span>
          {snackbar.competitionId && (
            <button
              type="button"
              onClick={() => handleViewCompetition(snackbar.competitionId)}
            >
              View Details
            </button>
          )}
        </div>
      )}
    </div>
  );
};

function getMediaSources(competition) {
  if (!competition) {
    return [];
  }

  if (Array.isArray(competition.mediaSources) && competition.mediaSources.length > 0) {
    return competition.mediaSources;
  }

  if (competition.youtubeLink) {
    return [{
      platform: 'youtube',
      url: competition.youtubeLink,
      videoId: competition.youtubeVideoId || extractYoutubeVideoId(competition.youtubeLink),
      type: 'full'
    }];
  }

  return [];
}

function buildHashtag(rawHashtag) {
  if (!rawHashtag) {
    return '#challenge';
  }

  return rawHashtag.startsWith('#') ? rawHashtag : `#${rawHashtag}`;
}

function extractYoutubeVideoId(urlValue) {
  if (!urlValue || typeof urlValue !== 'string') {
    return null;
  }

  try {
    const parsedUrl = new URL(urlValue);
    const host = parsedUrl.hostname.toLowerCase();

    if (host.includes('youtu.be')) {
      return parsedUrl.pathname.replace('/', '') || null;
    }

    if (host.includes('youtube.com')) {
      return parsedUrl.searchParams.get('v') || null;
    }
  } catch (error) {
    return null;
  }

  return null;
}

function createTransitionCard(competition) {
  return {
    id: `transition-${competition.id}-${Date.now()}`,
    type: 'transition',
    competitionId: competition.id,
    competitionName: competition.name || 'Competition'
  };
}

async function buildCardsForCompetition(competition) {
  const cards = [];
  const hashtag = buildHashtag(competition.requiredHashtag);
  const mediaSources = getMediaSources(competition);

  mediaSources.forEach((source, sourceIndex) => {
    const videoId = source.videoId || extractYoutubeVideoId(source.url);
    if (!videoId) {
      return;
    }

    cards.push({
      id: `media-${competition.id}-${sourceIndex}-${videoId}`,
      type: 'media',
      competitionId: competition.id,
      competitionName: competition.name || 'Competition',
      hashtag,
      sourceUrl: source.url,
      videoUrl: source.url,
      videoId
    });
  });

  try {
    const visibleData = await competitionService.getVisibleSubmissions(competition.id, {
      limit: CURATED_SUBMISSIONS_LIMIT,
      offset: 0
    });
    const curatedSubmissions = Array.isArray(visibleData.curatedSubmissions)
      ? visibleData.curatedSubmissions
      : [];

    curatedSubmissions.forEach((entry, entryIndex) => {
      const videoId = extractYoutubeVideoId(entry.videoUrl);
      if (!videoId) {
        return;
      }

      cards.push({
        id: `submission-${competition.id}-${entry.id || entryIndex}-${videoId}`,
        type: 'submission',
        competitionId: competition.id,
        competitionName: competition.name || 'Competition',
        hashtag,
        sourceUrl: entry.videoUrl,
        videoUrl: entry.videoUrl,
        videoId
      });
    });
  } catch (error) {
    console.error(`Unable to fetch curated submissions for ${competition.id}:`, error);
  }

  if (cards.length > 0) {
    return cards;
  }

  return [{
    id: `fallback-${competition.id}`,
    type: 'media',
    competitionId: competition.id,
    competitionName: competition.name || 'Competition',
    hashtag,
    sourceUrl: competition.youtubeLink || '',
    videoUrl: competition.youtubeLink || '',
    videoId: competition.youtubeVideoId || extractYoutubeVideoId(competition.youtubeLink)
  }];
}

export default ReelViewerPage;
